import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { showModal, hideModal } from 'store/modal/actions';
import { MODAL_TYPES } from 'constants/modal-types';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { logoutUser } from 'store/profile/actions';
import Platform from 'platform';
import i18n from '../i18n';

export type FocusBackConfigType = {
  sectionToFocus: string;
  elementToFocus?: string;
};

interface IShowModal {
  (focusBackConfig: FocusBackConfigType): void;
}

interface IEnterPinModal {
  (focusBackConfig: FocusBackConfigType, onModalConfirm: () => void): void;
}

interface IOnModalClose {
  (focusBackConfig: FocusBackConfigType): void;
}

const useModals = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const onModalClose = useCallback<IOnModalClose>(
    ({ sectionToFocus, elementToFocus }) => {
      dispatch(hideModal());
      if (sectionToFocus) SpatialNavigationWrapper.focus(sectionToFocus);
      if (elementToFocus) SpatialNavigationWrapper.focus(elementToFocus);
    },
    [dispatch],
  );

  const showLoginModal = useCallback<IShowModal>(
    (focusBackConfig) => {
      const actionData = showModal(MODAL_TYPES.CONFIRM, {
        message: i18n.t('profile.login.title'),
        description: i18n.t('profile.login.description'),
        confirmText: i18n.t('profile.login.confirmButton'),
        rejectText: i18n.t('profile.login.cancelButton'),
        options: [],
        onModalConfirm: () => {
          dispatch(hideModal());
          history.push('/login');
          onModalClose(focusBackConfig);
        },
        onModalBack: () => onModalClose(focusBackConfig),
      });
      dispatch(actionData);
    },
    [dispatch, history, onModalClose],
  );

  const showLogoutModal = useCallback<IShowModal>(
    (focusBackConfig) => {
      const actionData = showModal(MODAL_TYPES.CONFIRM, {
        message: i18n.t('profile.logout.title'),
        description: i18n.t('profile.logout.description'),
        confirmText: i18n.t('profile.logout.confirmButton'),
        rejectText: i18n.t('profile.logout.cancelButton'),
        onModalConfirm: () => {
          dispatch(hideModal());
          dispatch(logoutUser());
          onModalClose(focusBackConfig);
        },
        onModalBack: () => onModalClose(focusBackConfig),
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  const showEnterPinModal = useCallback<IEnterPinModal>(
    (focusBackConfig, onModalConfirm) => {
      const actionData = showModal(MODAL_TYPES.ENTER_PIN, {
        message: i18n.t('profile.pin.title'),
        description: i18n.t('profile.pin.description'),
        onModalConfirm: () => {
          dispatch(hideModal());
          onModalConfirm();
        },
        onModalBack: () => onModalClose(focusBackConfig),
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  const showExitModal = useCallback<IShowModal>(
    (focusBackConfig) => {
      const actionData = showModal(MODAL_TYPES.CONFIRM, {
        message: i18n.t('common.exitAppMessage'),
        confirmText: i18n.t('common.exit'),
        rejectText: i18n.t('common.cancel'),
        onModalConfirm: () => {
          Platform.exit();
          onModalClose(focusBackConfig);
        },
        onModalBack: () => onModalClose(focusBackConfig),
      });
      dispatch(actionData);
    },
    [dispatch, onModalClose],
  );

  return { showLoginModal, showLogoutModal, showPinModal: showEnterPinModal, showExitModal };
};

export default useModals;
